/**
 * Peer Valuation Bar Chart.
 *
 * Horizontal bar chart comparing target company + peers on a single
 * valuation metric at a time (P/E, Fwd P/E, P/B, EV/FCF).
 * Target company highlighted in accent color; peers in muted tones.
 */

import type Highcharts from "highcharts";
import { isDarkMode, setupThemeObserver } from "../../common/chartTheme";
import { createChartLifecycle, type ChartLifecycleReflowOptions } from "./chartLifecycle";
import { escapeHtml } from "./chartUtils";
import type { PeerChartData } from "./peerChartTypes";
import { showChartEmptyState } from "./peerChartTypes";

/** Metric key to chart data field mapping. */
type MetricKey = "per" | "fwdPe" | "pbv" | "evFcf";

const METRIC_LABELS: Record<MetricKey, string> = {
  per: "P/E",
  fwdPe: "Fwd P/E",
  pbv: "P/B",
  evFcf: "EV/FCF",
};

interface ValuationThemeColors {
  textColor: string;
  gridLineColor: string;
  accentColor: string;
  peerColor: string;
  tooltipBg: string;
  tooltipBorder: string;
  gradientStart: string;
  gradientEnd: string;
  dataLabelColor: string;
}

let themeObserver: MutationObserver | null = null;

function getThemeColors(): ValuationThemeColors {
  const dark = isDarkMode();
  return {
    textColor: dark ? "#E0E0E3" : "#333333",
    gridLineColor: dark ? "#374151" : "#e5e7eb",
    accentColor: dark ? "#60a5fa" : "#0F52BA",
    peerColor: dark ? "#6b7280" : "#9ca3af",
    tooltipBg: dark ? "rgba(30, 30, 30, 0.95)" : "rgba(255, 255, 255, 0.98)",
    tooltipBorder: dark ? "#404043" : "#cccccc",
    gradientStart: dark ? "#2dd4bf" : "#0d9488",
    gradientEnd: dark ? "#fbbf24" : "#d97706",
    dataLabelColor: dark ? "#d1d5db" : "#4b5563",
  };
}

/** Linearly interpolates between two hex colors. */
function interpolateColor(color1: string, color2: string, t: number): string {
  const r1 = Number.parseInt(color1.slice(1, 3), 16);
  const g1 = Number.parseInt(color1.slice(3, 5), 16);
  const b1 = Number.parseInt(color1.slice(5, 7), 16);
  const r2 = Number.parseInt(color2.slice(1, 3), 16);
  const g2 = Number.parseInt(color2.slice(3, 5), 16);
  const b2 = Number.parseInt(color2.slice(5, 7), 16);
  const r = Math.round(r1 + (r2 - r1) * t);
  const g = Math.round(g1 + (g2 - g1) * t);
  const b = Math.round(b1 + (b2 - b1) * t);
  return `#${r.toString(16).padStart(2, "0")}${g.toString(16).padStart(2, "0")}${b.toString(16).padStart(2, "0")}`;
}

function renderChartForContainer(container: HTMLElement): void {
  const configAttr = container.dataset.chartConfig;
  if (!configAttr) {
    lifecycle.destroyChart(container.id);
    return;
  }

  try {
    const scriptEl = document.getElementById(configAttr);
    if (!scriptEl) {
      lifecycle.destroyChart(container.id);
      return;
    }

    const data = JSON.parse(scriptEl.textContent || "{}") as PeerChartData;
    if (!data?.companies || data.companies.length === 0) {
      lifecycle.destroyChart(container.id);
      return;
    }

    if (!lifecycle.isContainerVisible(container)) {
      lifecycle.destroyChart(container.id);
      return;
    }

    const activeMetric = (container.dataset.activeMetric as MetricKey) || "per";

    lifecycle.destroyChart(container.id);
    const chart = renderValuationBarChart(container, data, activeMetric);
    if (chart) {
      lifecycle.instances.set(container.id, chart);
    }
  } catch (e) {
    console.warn(`Failed to render peer valuation chart for ${container.id}:`, e);
    showChartEmptyState(container, getThemeColors().textColor, "Chart could not be loaded");
  }
}

const lifecycle = createChartLifecycle(".peer-valuation-chart", renderChartForContainer, {
  deferUntilIntersect: true,
  rootMargin: "200px 0px",
});

/**
 * Renders a horizontal bar chart for the given metric.
 *
 * @param container - Target DOM element.
 * @param data - Parsed peer chart data.
 * @param metricKey - Which valuation metric to display.
 * @returns Highcharts chart instance or null.
 */
function renderValuationBarChart(
  container: HTMLElement,
  data: PeerChartData,
  metricKey: MetricKey,
): Highcharts.Chart | null {
  if (!container || !window.Highcharts) return null;

  const colors = getThemeColors();

  // Filter out companies without data for the selected metric, then sort ascending
  const entries = data.companies
    .filter((c) => c[metricKey] !== null)
    .sort((a, b) => (a[metricKey] as number) - (b[metricKey] as number));

  if (entries.length === 0) {
    container.style.height = "320px";
    showChartEmptyState(container, colors.textColor, "No data available for this metric");
    return null;
  }

  // Dynamic height based on company count
  const computedHeight = Math.max(320, entries.length * 24 + 80);
  container.style.height = `${computedHeight}px`;

  const targetIndex = entries.findIndex((c) => c.isTarget);
  const categories = entries.map((c) => `${c.name} (${c.ticker})`);
  const peerCount = entries.filter((c) => !c.isTarget).length;
  let peerIndex = 0;
  const seriesData = entries.map((c) => {
    if (c.isTarget) {
      return { y: c[metricKey] as number, color: colors.accentColor };
    }
    const t = peerCount > 1 ? peerIndex / (peerCount - 1) : 0.5;
    peerIndex++;
    return {
      y: c[metricKey] as number,
      color: interpolateColor(colors.gradientStart, colors.gradientEnd, t),
    };
  });

  const chart = window.Highcharts.chart(container, {
    chart: {
      type: "bar",
      backgroundColor: "transparent",
      spacing: [10, 15, 10, 10],
    },
    title: {
      text: METRIC_LABELS[metricKey],
      align: "left",
      style: { color: colors.textColor, fontSize: "14px", fontWeight: "600" },
    },
    credits: { enabled: false },
    exporting: { enabled: false },
    navigation: { buttonOptions: { enabled: false } },
    xAxis: {
      categories,
      labels: {
        useHTML: true,
        formatter() {
          const label = escapeHtml(this.value as string);
          if (this.pos === targetIndex) {
            return `<span style="font-weight:700;color:${colors.accentColor};">${label}</span>`;
          }
          return `<span>${label}</span>`;
        },
        style: { color: colors.textColor, fontSize: "11px" },
      },
      lineColor: colors.gridLineColor,
    },
    yAxis: {
      title: { text: null },
      gridLineColor: colors.gridLineColor,
      labels: {
        style: { color: colors.textColor, fontSize: "10px" },
      },
    },
    tooltip: {
      outside: true,
      useHTML: true,
      headerFormat: "",
      pointFormat:
        '<span style="font-weight:600;">{point.category}</span><br/>' +
        `${METRIC_LABELS[metricKey]}: <b>{point.y:.2f}</b>`,
      backgroundColor: colors.tooltipBg,
      borderColor: colors.tooltipBorder,
      style: { color: colors.textColor },
    },
    legend: { enabled: false },
    plotOptions: {
      bar: {
        borderRadius: 3,
        borderWidth: 0,
        animation: { duration: 400 },
        dataLabels: {
          enabled: true,
          format: "{point.y:.1f}",
          align: "left",
          overflow: "allow",
          crop: false,
          padding: 3,
          style: {
            fontSize: "10px",
            fontWeight: "normal",
            textOutline: "none",
            color: colors.dataLabelColor,
          },
        },
      },
    },
    series: [
      {
        type: "bar",
        name: METRIC_LABELS[metricKey],
        data: seriesData,
      },
    ],
  } as Highcharts.Options) as Highcharts.Chart;

  return chart;
}

/**
 * Updates the bar chart to show a different metric.
 * Called from the Alpine.js pill selector.
 *
 * @param metricKey - The metric key to switch to.
 */
const VALID_METRICS = new Set<string>(["per", "fwdPe", "pbv", "evFcf"]);

function updatePeerValuationMetric(metricKey: string, scopeEl?: Element): void {
  if (!VALID_METRICS.has(metricKey)) return;
  const root = scopeEl ?? document;
  const containers = root.querySelectorAll<HTMLElement>(".peer-valuation-chart");
  for (const container of containers) {
    if (!container.id) continue;
    container.dataset.activeMetric = metricKey;
    renderChartForContainer(container);
  }
}

// Expose to window for Alpine.js @click binding
declare global {
  interface Window {
    updatePeerValuationMetric?: (metricKey: string, scopeEl?: Element) => void;
  }
}
window.updatePeerValuationMetric = updatePeerValuationMetric;

/**
 * Initializes all peer valuation bar charts on the page.
 */
export function initializePeerValuationCharts(
  root: ParentNode = document,
  options: ChartLifecycleReflowOptions = {},
): void {
  const containers = lifecycle.getContainers(root);
  const activeIds = new Set<string>();

  containers.forEach((container) => {
    if (!container.id) {
      container.id = `peer-valuation-${crypto.randomUUID().slice(0, 8)}`;
    }
    activeIds.add(container.id);
    lifecycle.reflowChartForContainer(container, options);
  });

  lifecycle.pruneStaleCharts(activeIds, root);

  if (!themeObserver) {
    themeObserver = setupThemeObserver(() => {
      initializePeerValuationCharts(document, { forceRender: true });
    });
  }
}

/**
 * Registers DOM listeners to auto-initialize peer valuation charts.
 */
export function setupPeerValuationChartAutoInit(): void {
  lifecycle.setupAutoInit(initializePeerValuationCharts);
}
